import React, { useState, useEffect } from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import subscriptionService from '../services/subscriptionService';
import mixpanelService from '../services/mixpanelService';

function PricingPage() {
  const [products, setProducts] = useState([]);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [processingCheckout, setProcessingCheckout] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);

  useEffect(() => {
    mixpanelService.track('Pricing Viewed');
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [productsResponse, subscriptionData] = await Promise.all([
        subscriptionService.getProducts(),
        subscriptionService.getSubscriptionInfo()
      ]);
      
      // Products response now includes user_role
      setProducts(productsResponse.products || productsResponse);
      setCurrentSubscription(subscriptionData);
    } catch (error) {
      console.error('Error loading pricing data:', error);
      setLoadError('Live pricing is temporarily unavailable. No charge has been attempted.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (product, period) => {
    try {
      setProcessingCheckout(true);
      mixpanelService.track('Plan Selected', {
        plan_name: product.name,
        billing_period: period
      });
      await subscriptionService.createCheckoutSession(period);
    } catch (error) {
      console.error('Error starting checkout:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setProcessingCheckout(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setProcessingCheckout(true);
      await subscriptionService.createBillingPortal();
    } catch (error) {
      console.error('Error opening billing portal:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setProcessingCheckout(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pricing...</p>
        </div>
      </div>
    );
  }

  const hasActiveSubscription = currentSubscription?.status === 'active' ||
                                currentSubscription?.status === 'trialing';
  const selectedYearlyPrice = products[0]?.prices?.yearly;
  const trialDays = products[0]?.trial_days || 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            {trialDays > 0
              ? `Start with a ${trialDays}-day free trial. Cancel anytime.`
              : 'Choose monthly or annual billing. Cancel anytime.'}
          </p>

          {/* Billing Period Toggle */}
          <div className="flex items-center justify-center mb-8">
            <span className={`mr-3 ${billingPeriod === 'monthly' ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
              Monthly
            </span>
            <button
              onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
              className="relative inline-flex h-6 w-11 items-center rounded-full bg-purple-600 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  billingPeriod === 'yearly' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`ml-3 ${billingPeriod === 'yearly' ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
              Yearly
              {selectedYearlyPrice?.savings_amount > 0 && (
                <span className="ml-1 text-green-600 text-sm font-semibold">
                  Save {subscriptionService.formatPrice(
                    selectedYearlyPrice.savings_amount,
                    selectedYearlyPrice.currency
                  )}
                </span>
              )}
            </span>
          </div>
        </div>

        {/* Current Subscription Status */}
        {hasActiveSubscription && (
          <div className="mb-8 bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Current Subscription</h3>
                <p className="text-gray-600">
                  {currentSubscription.status === 'trialing' ? 'Trial' : 'Active plan'}
                </p>
                {currentSubscription.status === 'trialing' &&
                 currentSubscription.trial_days_remaining && (
                  <p className="text-sm text-purple-600 mt-1">
                    {currentSubscription.trial_days_remaining} days left in trial
                  </p>
                )}
              </div>
              <button
                onClick={handleManageSubscription}
                disabled={processingCheckout}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
              >
                Manage Subscription
              </button>
            </div>
          </div>
        )}

        {loadError && (
          <div className="mx-auto mb-8 max-w-2xl rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-center text-yellow-900">
            {loadError}
          </div>
        )}

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {products.map((product) => {
            const price = product.prices[billingPeriod];
            const isCurrentPlan = hasActiveSubscription;
            
            return (
              <div
                key={product.id}
                className={`bg-white rounded-2xl shadow-xl p-8 relative ${
                    'border-2 border-purple-500'
                }`}
              >
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">{product.name}</h2>
                  <div className="flex items-baseline justify-center">
                    <span className="text-5xl font-bold text-gray-900">
                      {subscriptionService.formatPrice(price.amount, price.currency)}
                    </span>
                    <span className="text-gray-500 ml-2">/{billingPeriod === 'monthly' ? 'month' : 'year'}</span>
                  </div>
                  {billingPeriod === 'yearly' && (
                    <p className="text-sm text-green-600 mt-2">
                      {price.savings_amount > 0
                        ? `Save ${subscriptionService.formatPrice(price.savings_amount, price.currency)} compared with twelve monthly payments`
                        : 'Annual billing'}
                    </p>
                  )}
                </div>

                <ul className="space-y-4 mb-8">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckIcon className="h-5 w-5 text-green-500 mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleSelectPlan(product, billingPeriod)}
                  disabled={processingCheckout || isCurrentPlan}
                  className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${
                    isCurrentPlan
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg hover:shadow-xl'
                  }`}
                >
                  {processingCheckout ? (
                    <span className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Processing...
                    </span>
                  ) : isCurrentPlan ? (
                    'Current Plan'
                  ) : (
                    trialDays > 0 ? 'Start Free Trial' : 'Continue to Checkout'
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-6">
            <div className="bg-white rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                When will I be charged?
              </h3>
              <p className="text-gray-600">
                Stripe Checkout shows the exact amount and charge date before you subscribe.
                {trialDays > 0
                  ? ` Your first charge is after the ${trialDays}-day trial unless you cancel first.`
                  : ' Your subscription begins when you confirm payment.'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                Can I change my plan later?
              </h3>
              <p className="text-gray-600">
                You can update payment details or cancel through the secure Stripe billing portal in account settings. Available plan changes are shown there.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                Do you offer refunds?
              </h3>
              <p className="text-gray-600">
                Charges are generally non-refundable except where required by law or expressly stated at checkout. Contact support if you believe a charge was made in error.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PricingPage;
